import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Save } from "lucide-react";
import { toast } from "sonner";

import Sidebar, { MobileProgressBar } from "@/components/enrollment/Sidebar";
import EmailGate from "@/components/enrollment/EmailGate";
import { STEPS } from "@/lib/enrollment/stepsConfig";
import {
  fetchEnrollment,
  saveEnrollment,
  submitEnrollment,
  createCheckout,
} from "@/lib/enrollment/api";

import {
  WelcomeStep,
  AgreementStep,
  DocumentsStep,
  TuitionStep,
  ParentInfoStep,
  StudentInfoStep,
  MedicalStep,
  LiabilityStep,
  MediaStep,
  AcademicReleaseStep,
  ExpectationsStep,
  TechIntegrityStep,
  UniformsStep,
  LoginsStep,
  OrientationStep,
} from "@/components/enrollment/steps";

const STORAGE_KEY = "prsvr.enrollment.email";

export default function EnrollmentPortal({ compFamily = null, compFamilyLabel = null }) {
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [enrollment, setEnrollment] = useState(null);
  const [data, setData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const saveTimer = useRef(null);

  // Load enrollment when email is known
  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    setLoading(true);
    fetchEnrollment(email)
      .then(async (doc) => {
        if (cancelled) return;
        // If this is a comp URL and the enrollment isn't already flagged, flip
        // it server-side immediately so the sidebar / gate render as paid.
        if (compFamily && doc?.payment_status !== "paid" && doc?.comp_family !== compFamily) {
          try {
            const flipped = await saveEnrollment({
              email,
              current_step: doc.current_step || 0,
              data: doc.data || {},
              comp_family: compFamily,
            });
            doc = flipped || doc;
          } catch (e) {
            console.error("[comp] flip failed", e);
          }
        }
        setEnrollment(doc);
        setData(doc.data || {});
        setCurrentStep(Math.max(0, Math.min(STEPS.length - 1, doc.current_step || 0)));
        if (doc.status === "submitted") setSubmitted(true);
      })
      .catch((e) => {
        console.error(e);
        toast.error("Could not load your enrollment. Please try again.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [email]);

  // Detect return from Stripe (handled by /payment-return route which then
  // sends the user back to "/" — we just need to re-fetch to pick up paid status)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1" && email) {
      fetchEnrollment(email).then((doc) => {
        setEnrollment(doc);
        setData((d) => ({ ...d, ...(doc.data || {}) }));
        toast.success("Deposit received. Your seat is reserved.");
      });
      // strip param
      window.history.replaceState({}, "", "/");
    }
    if (params.get("cancelled") === "1") {
      toast.message("Payment cancelled. You can try again any time.");
      window.history.replaceState({}, "", "/");
    }
  }, [email]);

  // Debounced auto-save
  const persist = useCallback(
    (nextData, nextStep) => {
      if (!email) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          const doc = await saveEnrollment({
            email,
            current_step: nextStep,
            data: nextData,
            comp_family: compFamily || undefined,
          });
          setEnrollment(doc);
        } catch (e) {
          console.error(e);
        }
      }, 600);
    },
    [email, compFamily],
  );

  const set = useCallback(
    (key, value) => {
      setData((prev) => {
        const next = { ...prev, [key]: value };
        persist(next, currentStep);
        return next;
      });
    },
    [persist, currentStep],
  );

  const goTo = useCallback(
    (idx) => {
      const next = Math.max(0, Math.min(STEPS.length - 1, idx));
      setCurrentStep(next);
      persist(data, next);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [data, persist],
  );

  const startWithEmail = (e) => {
    try {
      localStorage.setItem(STORAGE_KEY, e);
    } catch {}
    setEmail(e);
  };

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await createCheckout({
        email,
        enrollment_id: enrollment?.enrollment_id,
        plan: data.selected_plan || "full",
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Checkout failed.");
    } finally {
      setPaying(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await submitEnrollment(email);
      if (res?.status === "submitted") {
        setSubmitted(true);
        setEnrollment((prev) => ({ ...(prev || {}), ...res }));
        toast.success("Enrollment submitted — check your email for the signed PDF.");
      }
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(detail || "Enrollment fee required.", { duration: 8000 });
      } else {
        toast.error(detail || "Submission failed. Please review and try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const completedSteps = useMemo(() => {
    const set = new Set();
    for (let i = 0; i < currentStep; i++) set.add(i);
    if (submitted) for (let i = 0; i < STEPS.length; i++) set.add(i);
    return set;
  }, [currentStep, submitted]);

  if (!email) return <EmailGate onStart={startWithEmail} loading={loading} />;

  const stepNode = renderStep(STEPS[currentStep].id, {
    data,
    set,
    email,
    enrollment,
    onPay: handlePay,
    paying,
    onSubmit: handleSubmit,
    submitting,
    submitted,
    compFamily,
    compFamilyLabel,
  });

  return (
    <div className="flex min-h-screen bg-cream-grain">
      <Sidebar
        currentStep={currentStep}
        completedSteps={completedSteps}
        onJump={goTo}
        paymentStatus={enrollment?.payment_status}
      />
      <main className="flex-1 min-w-0 flex flex-col">
        {compFamily && (
          <div
            className="w-full bg-emerald-700 text-white text-[11px] sm:text-xs font-semibold tracking-wide px-5 sm:px-10 py-2.5 flex items-center justify-center gap-2 uppercase"
            data-testid="comp-family-banner"
          >
            <span className="inline-flex w-2 h-2 bg-emerald-300 rounded-full animate-pulse" />
            {compFamilyLabel || "Comp Enrollment"} · Tuition paid separately · No payment required to submit
          </div>
        )}
        <MobileProgressBar currentStep={currentStep} />
        <div className="flex-1 px-5 sm:px-10 md:px-14 lg:px-20 py-10 md:py-16 max-w-[1100px] w-full mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              data-testid={`step-content-${STEPS[currentStep].id}`}
            >
              {stepNode}
            </motion.div>
          </AnimatePresence>

          {!submitted && (
            <div className="mt-12 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4">
              <button
                onClick={() => goTo(currentStep - 1)}
                disabled={currentStep === 0}
                data-testid="nav-back-btn"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#1B3B2B] text-[#1B3B2B] hover:bg-[#1B3B2B] hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#1B3B2B] transition-colors rounded-sm px-6 py-3 font-semibold uppercase tracking-wider text-xs"
              >
                <ArrowLeft size={16} /> Back
              </button>

              <div className="flex items-center gap-2 text-xs text-[#4B5563]">
                <Save size={14} /> Saved automatically
              </div>

              {currentStep < STEPS.length - 1 ? (
                <button
                  onClick={() => goTo(currentStep + 1)}
                  data-testid="nav-next-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#1B3B2B] text-white hover:bg-[#132A13] transition-colors rounded-sm px-7 py-3 font-semibold uppercase tracking-wider text-xs"
                >
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <div />
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function renderStep(id, props) {
  switch (id) {
    case "welcome":
      return <WelcomeStep {...props} />;
    case "agreement":
      return <AgreementStep {...props} />;
    case "documents":
      return <DocumentsStep {...props} />;
    case "tuition":
      return <TuitionStep {...props} />;
    case "parent":
      return <ParentInfoStep {...props} />;
    case "student":
      return <StudentInfoStep {...props} />;
    case "medical":
      return <MedicalStep {...props} />;
    case "liability":
      return <LiabilityStep {...props} />;
    case "media":
      return <MediaStep {...props} />;
    case "academic":
      return <AcademicReleaseStep {...props} />;
    case "expectations":
      return <ExpectationsStep {...props} />;
    case "tech":
      return <TechIntegrityStep {...props} />;
    case "uniforms":
      return <UniformsStep {...props} />;
    case "logins":
      return <LoginsStep {...props} />;
    case "orientation":
      return <OrientationStep {...props} />;
    default:
      return null;
  }
}
